import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert } from '@material-ui/lab'
import { Grid } from '@material-ui/core'
import SydButton from '../../../../../commonDesign/Button'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import { decipherError } from '../../../../../../utils/decipherError'
import { dataCatalog } from '../../../../../../api/coreData'
import SydLabel from '../../../../../commonDesign/SydLabel'
import Loading from '../../../../../molecules/Loading'
import SydSelect from '../../../../../commonDesign/SydSelect'
import OpalHeading from '../../../../../opal/OpalHeading'
import FlexRow from '../../../../../molecules/FlexRow'

const useSubmitter = (dataSource, onComplete) => {
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const tableMutation = dataCatalog.useModifyDatasourceRestrictions()
  const apiMutation = dataCatalog.useModifyDatasourceApiInputRestrictions()

  const onSubmit = useCallback(async (formData) => {
    const command = {
      datasourceId: dataSource.catalogDatasourceId,
      restrictions: formData.restrictions
    }

    try {
      setError(null)
      setProcessing(true)

      const mutation = dataSource.type === 'api' ? apiMutation : tableMutation
      const result = await mutation.mutateAsync(command)
      if (result?.statusCode === 500) {
        setError('Something unexpected happened while updating the datasource restrictions')
        return
      }

      onComplete(result)
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [dataSource, onComplete, tableMutation, apiMutation])

  return {
    onSubmit,
    processing,
    error
  }
}

function ModifyDatasourceRestrictionsForm ({ dataSource, onCancel, onComplete }) {
  // const classes = useStyles()
  const { data, isLoading } = dataCatalog.useListCatalogRestrictionTypes()

  /** Form iterates over these */
  const restrictionTypes = useMemo(() => {
    return (data?.restrictionTypes || []).map(rt => ({
      key: `rt_${rt.restrictionType}`,
      ...rt
    }))
  }, [data])

  /** Options for the selects - different for table vs API */
  const restrictionOptions = useMemo(() => {
    if (dataSource.type === 'api') {
      return (dataSource.apiInputs || []).map(input => ({
        label: input.inputName,
        value: input.catalogApiInputId
      }))
    }
    // Table type
    return (dataSource.datapoints || []).map(dp => ({
      label: dp.accessor,
      value: dp.catalogDatapointId
    }))
  }, [dataSource])

  /** Form State */
  const [restrictions, setRestrictions] = useState(dataSource.restrictions)

  /** Get a value for a particular restriction type */
  const getRestrictionValueForType = useCallback((rt) => {
    const sourceRestriction = (dataSource.restrictions || []).find(x => x.type === rt.restrictionType)
    const currentRestriction = (restrictions || []).find(x => x.type === rt.restrictionType)

    const fromValue = dataSource.type === 'api'
      ? (sourceRestriction?.catalogApiInputId || null)
      : (sourceRestriction?.datapointId || null)

    const toValue = dataSource.type === 'api'
      ? (currentRestriction?.catalogApiInputId || null)
      : (currentRestriction?.datapointId || null)

    return { from: fromValue, to: toValue }
  }, [restrictions, dataSource])

  /** Set the new state on changes */
  const setRestrictionValueForType = useCallback((restrictionType) => (e) => {
    const type = restrictionType.restrictionType
    const value = e.target.value
    setRestrictions(prev => {
      const copy = [...(prev || [])]
      if (copy.some(x => x.type === type)) {
        const index = copy.findIndex(x => x.type === type)
        if (!value) {
          copy.splice(index, 1)
        } else {
          // Update the appropriate field based on datasource type
          if (dataSource.type === 'api') {
            copy[index].catalogApiInputId = value
            // Remove datapointId if it exists (from old data)
            delete copy[index].datapointId
          } else {
            copy[index].datapointId = value
            // Remove catalogApiInputId if it exists (from old data)
            delete copy[index].catalogApiInputId
          }
        }
      } else {
        // Create new restriction with appropriate field
        const newRestriction = {
          datasourceId: dataSource.catalogDatasourceId,
          type
        }
        if (dataSource.type === 'api') {
          newRestriction.catalogApiInputId = value
        } else {
          newRestriction.datapointId = value
        }
        copy.push(newRestriction)
      }

      return copy
    })
  }, [setRestrictions, dataSource])

  const clearRestrictionForType = useCallback((restrictionType) => () => {
    const type = restrictionType.restrictionType
    setRestrictions(prev => {
      const copy = [...(prev || [])]
      if (copy.some(x => x.type === type)) {
        const index = copy.findIndex(x => x.type === type)
        copy.splice(index, 1)
      }
      return copy
    })
  }, [setRestrictions])
  const { onSubmit, processing, error } = useSubmitter(dataSource, onComplete)

  if (isLoading) {
    return (<Loading />)
  }

  if (!dataSource) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Alert severity='error'>No data source provided</Alert>
        </Grid>
      </Grid>
    )
  }

  return (
    <div>
      <Grid container spacing={2}>
        {restrictionTypes.map(rt => {
          const values = getRestrictionValueForType(rt)
          return (
            <Grid key={rt.key} item xs={12}>
              <OpalHeading className='font-size-sm'>{rt.name}</OpalHeading>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <SydLabel label='From'>
                    <SydSelect
                      disabled
                      options={restrictionOptions}
                      value={values.from}
                      size='sm'
                    />
                  </SydLabel>
                </Grid>
                <Grid item xs={6}>
                  <SydLabel label='To'>
                    <FlexRow>
                      <SydSelect
                        options={restrictionOptions}
                        value={values.to}
                        onChange={setRestrictionValueForType(rt)}
                        size='sm'
                      />
                      <SydButton size='xs' variant='outline' icon='close' onClick={clearRestrictionForType(rt)} disabled={!values.to}>Remove</SydButton>
                    </FlexRow>
                  </SydLabel>
                </Grid>
              </Grid>
            </Grid>
          )
        })}

        {/* Actions */}
        <Grid item xs={12}>
          <SydModalActions>
            {error && <div className='__error'>{error}</div>}
            <SydButton
              disabled={processing}
              variant='ghost'
              size='md'
              onClick={onCancel}
            >
              Cancel
            </SydButton>
            <SydButton
              processing={processing}
              variant='primary'
              size='md'
              onClick={() => onSubmit({ restrictions })}
            >
              Save Changes
            </SydButton>
          </SydModalActions>
        </Grid>
      </Grid>
    </div>
  )
}

ModifyDatasourceRestrictionsForm.propTypes = {
  dataSource: PropTypes.shape({
    catalogDatasourceId: PropTypes.number.isRequired,
    displayName: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    subType: PropTypes.string.isRequired,
    description: PropTypes.string,
    schema: PropTypes.shape({
      code: PropTypes.string
    }),
    restrictions: PropTypes.arrayOf(PropTypes.shape({
      type: PropTypes.string,
      datapointId: PropTypes.number,
      catalogApiInputId: PropTypes.number
    })),
    datapoints: PropTypes.arrayOf(PropTypes.shape({
      catalogDatapointId: PropTypes.number,
      accessor: PropTypes.string
    })),
    apiInputs: PropTypes.arrayOf(PropTypes.shape({
      catalogApiInputId: PropTypes.number,
      inputName: PropTypes.string,
      displayName: PropTypes.string
    }))
  }).isRequired,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

export default ModifyDatasourceRestrictionsForm
